import { Component } from '@angular/core';
import { FormBuilder, Validators, FormGroup, FormControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService, LoginResult } from '../../_core/_services/auth.service';
import { TokenStorageService } from '../../_core/_services/token-storage.service';
import { NotificationService } from '../../_core/_services/notification.service';
import { defaultRouteForRole, safeInternalReturnUrl } from '../../_core/_helpers/role-navigation.helper';

type LoginMode = 'password' | 'pin';

type LoginForm = FormGroup<{
  email: FormControl<string>;
  password: FormControl<string>;
}>;

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.html',
  styleUrl: './login.css',
})
export class Login {
  loading = false;
  submitted = false;
  error = '';
  mode: LoginMode = 'password';
  loginPin = '';

  form: LoginForm;

  constructor(
    private notifyService: NotificationService,
    private fb: FormBuilder,
    private auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private tokens: TokenStorageService
  ) {
    this.form = this.fb.nonNullable.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
    });

    const token = this.auth.accessToken;
    const exp = this.tokens.getAccessExpiresAt?.();
    if (token && (!exp || new Date(exp).getTime() > Date.now())) {
      this.auth.getCurrentUser().subscribe(user => {
        this.router.navigateByUrl(this.safeReturnUrl(user?.role));
      });
    }
  }

  setMode(mode: LoginMode): void {
    this.mode = mode;
    this.submitted = false;
    this.error = '';
    this.loginPin = '';

    const password = this.form.controls.password;

    if (mode === 'password') {
      password.setValidators([Validators.required, Validators.minLength(6)]);
    } else {
      password.clearValidators();
      password.setValue('');
    }

    password.updateValueAndValidity();
  }

  submit(pinOverride?: string): void {
    this.submitted = true;
    this.error = '';

    const pin = (pinOverride ?? this.loginPin).trim();

    if (this.form.invalid || this.loading) {
      return;
    }

    if (this.mode === 'pin' && !/^\d{6}$/.test(pin)) {
      this.error = 'Enter your six-digit account PIN.';
      return;
    }

    this.loading = true;
    this.tokens.clearTotpChallengeToken();

    const { email, password } = this.form.getRawValue();
    const request$ = this.mode === 'pin'
      ? this.auth.loginWithPin(email, pin)
      : this.auth.login(email, password);

    request$.subscribe({
      next: result => this.handleLoginResult(result),
      error: err => {
        this.error = this.apiMessage(err, 'Login failed. Please check your credentials.');
        this.loginPin = '';
        this.notifyService.showError(this.error, 'Login');

        const errorCode = err?.error?.error?.code || err?.error?.Error?.Code;
        if (errorCode === 'ACCOUNT_INACTIVE') {
          this.openReactivationHelp();
        }

        this.loading = false;
      },
      complete: () => this.loading = false,
    });
  }

  openReactivationHelp(): void {
    window.dispatchEvent(new CustomEvent('swipfx-support-open', {
      detail: {
        question: 'I want to reactivate my deactivated account',
        email: this.form.controls.email.value.trim()
      }
    }));
  }

  private handleLoginResult(result: LoginResult): void {
    if (result.kind === 'totp') {
      const returnUrl = safeInternalReturnUrl(this.route.snapshot.queryParamMap.get('returnUrl'));
      this.router.navigate(['/auth/tfa'], {
        queryParams: returnUrl ? { returnUrl } : undefined
      });
      return;
    }

    const data = result.data;
    const user = data.user;

    if (data.requiresPasswordChange) {
      this.router.navigateByUrl('/auth/change-password');
      return;
    }

    const returnUrl = this.safeReturnUrl(user.role);
    if (user.otp && !user.otp.otpEnabled && user.otp.otpNextReminder) {
      const next = new Date(user.otp.otpNextReminder).getTime();
      if (next <= Date.now()) {
        this.router.navigate(['/auth/2fa-reminder'], { queryParams: { returnUrl } });
        return;
      }
    }

    this.router.navigateByUrl(returnUrl);
  }

  private safeReturnUrl(role?: string | null): string {
    return safeInternalReturnUrl(this.route.snapshot.queryParamMap.get('returnUrl'))
      ?? defaultRouteForRole(role);
  }

  private apiMessage(err: any, fallback: string): string {
    return err?.error?.message
      || err?.error?.Message
      || err?.error?.error?.details
      || err?.error?.Error?.Details
      || fallback;
  }
}
